import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth.hook';
import { ROUTES } from '@/router/routes.const';
import { AppFeature, AppAction, permissionFor } from '@/types/auth.types';
import { usePermissions, useHasPermission } from '@/context/permissions.context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Users,
  DollarSign,
  BarChart3,
  Wrench,
  Package,
  ShoppingCart,
  Factory,
  HelpCircle,
  LogOut,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/shared/theme-toggle';
import { LanguageSwitcher } from '@/components/shared/language-switcher';

// ---- Navigation definition ------------------------------------------------

interface NavItem {
  path?: string;
  labelKey: string;
  icon?: React.ReactNode;
  feature?: string;
  children?: NavItem[];
}

function leafPaths(item: NavItem): string[] {
  if (!item.children) return item.path ? [item.path] : [];
  return item.children.flatMap(leafPaths);
}

function filterItem(
  item: NavItem,
  canRead: (f?: string) => boolean
): NavItem | null {
  if (!item.children) {
    return canRead(item.feature) ? item : null;
  }
  const visibleChildren = item.children
    .map((c) => filterItem(c, canRead))
    .filter(Boolean) as NavItem[];
  return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
}

function useNavItems() {
  const permissions = usePermissions();
  const canRead = (feature?: string) =>
    !feature || permissions.has(permissionFor(feature as any, AppAction.Read));

  const allItems: NavItem[] = [
    {
      path: ROUTES.dashboard,
      labelKey: 'navigation.dashboard',
      icon: <LayoutDashboard className="h-4 w-4" aria-hidden="true" />,
    },
    {
      path: ROUTES.personnel.root,
      labelKey: 'navigation.personnel',
      icon: <Users className="h-4 w-4" aria-hidden="true" />,
      children: [
        { path: ROUTES.personnel.employees, labelKey: 'navigation.employees', feature: AppFeature.Employee },
        { path: ROUTES.personnel.employmentList, labelKey: 'navigation.employments', feature: AppFeature.Employment },
        { path: ROUTES.personnel.deductionsOverview, labelKey: 'navigation.deductions', feature: AppFeature.PayrollDeduction },
        { path: ROUTES.personnel.leaveManagement, labelKey: 'navigation.leaveManagement', feature: AppFeature.Employee },
      ],
    },
    {
      path: ROUTES.payroll.root,
      labelKey: 'navigation.payroll',
      icon: <DollarSign className="h-4 w-4" aria-hidden="true" />,
      children: [
        { path: ROUTES.payroll.cycles, labelKey: 'navigation.payrollCycles', feature: AppFeature.PayrollCycle },
        { path: ROUTES.payroll.millingBatches, labelKey: 'navigation.millingBatches', feature: AppFeature.PayrollCycle },
        { path: ROUTES.payroll.loanDeductions,  labelKey: 'navigation.loanDeductions',  feature: AppFeature.PayrollDeduction },
        { path: ROUTES.payroll.prepaymentFunds,   labelKey: 'navigation.prepaymentFunds',   feature: AppFeature.PayrollDeduction },
        { path: ROUTES.payroll.operationalExpenses, labelKey: 'navigation.operationalExpenses', feature: AppFeature.OperationalExpense },
      ],
    },
    {
      path: ROUTES.inventory.root,
      labelKey: 'navigation.inventory',
      icon: <Package className="h-4 w-4" aria-hidden="true" />,
      children: [
        { path: ROUTES.inventory.procurements, labelKey: 'navigation.procurement', feature: AppFeature.Inventory },
        { path: ROUTES.inventory.requisitions, labelKey: 'navigation.requisitions', feature: AppFeature.Requisition },
        { path: ROUTES.inventory.stock, labelKey: 'navigation.stockLevels', feature: AppFeature.Inventory },
      ],
    },
    {
      labelKey: 'navigation.processing',
      icon: <Factory className="h-4 w-4" aria-hidden="true" />,
      children: [
        {
          labelKey: 'navigation.salesProcessing',
          children: [
            { path: ROUTES.sales.oilPurchases, labelKey: 'navigation.oilPurchases' },
            { path: ROUTES.sales.oilAllocations, labelKey: 'navigation.oilAllocations' },
            { path: ROUTES.sales.oilSterilizations, labelKey: 'navigation.oilSterilizations' },
          ],
        },
        {
          labelKey: 'navigation.salesProduction',
          children: [
            { path: ROUTES.sales.skus,              labelKey: 'navigation.skus',              feature: AppFeature.Sku },
            { path: ROUTES.sales.bottlingBatches,   labelKey: 'navigation.bottlingBatches',   feature: AppFeature.BottlingBatch },
            { path: ROUTES.sales.cartonningBatches, labelKey: 'navigation.cartonningBatches', feature: AppFeature.CartonningBatch },
          ],
        },
        {
          labelKey: 'navigation.millStock',
          children: [
            { path: ROUTES.sales.cpoInventory,    labelKey: 'navigation.cpoInventory',  feature: AppFeature.PayrollCycle },
            { path: ROUTES.sales.finishedGoods,   labelKey: 'navigation.finishedGoods' },
            { path: ROUTES.payroll.cpkoInventory, labelKey: 'navigation.cpkoInventory', feature: AppFeature.PayrollCycle },
            { path: ROUTES.payroll.pkcInventory,  labelKey: 'navigation.pkcInventory',  feature: AppFeature.PayrollCycle },
          ],
        },
      ],
    },
    {
      labelKey: 'navigation.sales',
      icon: <ShoppingCart className="h-4 w-4" aria-hidden="true" />,
      children: [
        { path: ROUTES.payroll.farmSalesOverview, labelKey: 'navigation.farmSales', feature: AppFeature.PayrollDeduction },
        { path: ROUTES.sales.saleOrders,          labelKey: 'navigation.saleOrders' },
        { path: ROUTES.sales.customers,           labelKey: 'navigation.customers', feature: AppFeature.Customer },
      ],
    },
    {
      path: ROUTES.reporting.root,
      labelKey: 'navigation.reporting',
      icon: <BarChart3 className="h-4 w-4" aria-hidden="true" />,
      children: [
        {
          labelKey: 'navigation.reportingPayroll',
          children: [
            { path: ROUTES.reporting.payrollSummary,      labelKey: 'navigation.reportPayrollSummary',      feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.payslip,             labelKey: 'navigation.reportPayslip',             feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.payrollDisbursement, labelKey: 'navigation.reportPayrollDisbursement', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.taskDetail, labelKey: 'navigation.reportTaskDetail', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.millingReport, labelKey: 'navigation.reportMillingReport', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.operationRatesCard, labelKey: 'navigation.reportOperationRatesCard', feature: AppFeature.OperationRate },
            { path: ROUTES.reporting.productionReport, labelKey: 'navigation.reportProductionReport', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.costOfProduction, labelKey: 'navigation.reportCostOfProduction', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.ffbCost, labelKey: 'navigation.reportFfbCost', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.cpoCost, labelKey: 'navigation.reportCpoCost', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.labourProductivity, labelKey: 'navigation.reportLabourProductivity', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.blockPerformance, labelKey: 'navigation.reportBlockPerformance', feature: AppFeature.PayrollCycle },
            { path: ROUTES.reporting.annualSummary, labelKey: 'navigation.reportAnnualSummary', feature: AppFeature.PayrollCycle },
          ],
        },
        {
          labelKey: 'navigation.reportingPersonnel',
          children: [
            { path: ROUTES.reporting.employeeRegister, labelKey: 'navigation.reportEmployeeRegister', feature: AppFeature.Employee },
            { path: ROUTES.reporting.headcount, labelKey: 'navigation.reportHeadcount', feature: AppFeature.Employment },
            { path: ROUTES.reporting.advancesLoans, labelKey: 'navigation.reportAdvancesLoans', feature: AppFeature.PayrollDeduction },
            { path: ROUTES.reporting.deductionAging, labelKey: 'navigation.reportDeductionAging', feature: AppFeature.PayrollDeduction },
            { path: ROUTES.reporting.employmentHistoryReport, labelKey: 'navigation.reportEmploymentHistory', feature: AppFeature.Employment },
          ],
        },
        {
          labelKey: 'navigation.reportingEstates',
          children: [
            { path: ROUTES.reporting.estateProfile, labelKey: 'navigation.reportEstateProfile', feature: AppFeature.Estate },
            { path: ROUTES.reporting.blockProductivity, labelKey: 'navigation.reportBlockProductivity', feature: AppFeature.Block },
            { path: ROUTES.reporting.operationPerformance, labelKey: 'navigation.reportOperationPerformance', feature: AppFeature.Operation },
          ],
        },
        {
          labelKey: 'navigation.reportingInventory',
          children: [
            { path: ROUTES.reporting.stockLevels, labelKey: 'navigation.reportStockLevels', feature: AppFeature.Inventory },
            { path: ROUTES.reporting.stockMovement, labelKey: 'navigation.reportStockMovement', feature: AppFeature.Inventory },
            { path: ROUTES.reporting.procurementReport, labelKey: 'navigation.reportProcurement', feature: AppFeature.Inventory },
            { path: ROUTES.reporting.requisitionsReport, labelKey: 'navigation.reportRequisitions', feature: AppFeature.Requisition },
            { path: ROUTES.reporting.vendorSummary, labelKey: 'navigation.reportVendorSummary', feature: AppFeature.Vendor },
          ],
        },
        {
          labelKey: 'navigation.reportingExpenses',
          children: [
            { path: ROUTES.reporting.expenseSummary, labelKey: 'navigation.reportExpenseSummary', feature: AppFeature.OperationalExpense },
            { path: ROUTES.reporting.expenseDetail, labelKey: 'navigation.reportExpenseDetail', feature: AppFeature.OperationalExpense },
            { path: ROUTES.reporting.expenseByEstate, labelKey: 'navigation.reportExpenseByEstate', feature: AppFeature.OperationalExpense },
          ],
        },
        {
          labelKey: 'navigation.reportingSales',
          children: [
            { path: ROUTES.reporting.salesInventoryBalances, labelKey: 'navigation.reportSalesInventoryBalances', feature: AppFeature.SalesReport },
            { path: ROUTES.reporting.productionVsAllocation, labelKey: 'navigation.reportProductionVsAllocation', feature: AppFeature.SalesReport },
            { path: ROUTES.reporting.bottlingEfficiency, labelKey: 'navigation.reportBottlingEfficiency', feature: AppFeature.SalesReport },
            { path: ROUTES.reporting.rejectionRate, labelKey: 'navigation.reportRejectionRate', feature: AppFeature.SalesReport },
            { path: ROUTES.reporting.marginByChannel, labelKey: 'navigation.reportMarginByChannel', feature: AppFeature.SalesReport },
            { path: ROUTES.reporting.fgTurnover, labelKey: 'navigation.reportFgTurnover', feature: AppFeature.SalesReport },
            { path: ROUTES.reporting.oilPurchaseReport, labelKey: 'navigation.reportOilPurchases', feature: AppFeature.SalesReport },
            { path: ROUTES.reporting.marketPriceStrategy, labelKey: 'navigation.reportMarketPriceStrategy', feature: AppFeature.SalesReport },
          ],
        },
      ],
    },
    {
      path: ROUTES.settings.root,
      labelKey: 'navigation.setup',
      icon: <Wrench className="h-4 w-4" aria-hidden="true" />,
      children: [
        {
          labelKey: 'navigation.inventory',
          children: [
            { path: ROUTES.inventory.items, labelKey: 'navigation.inventoryItems', feature: AppFeature.Inventory },
            { path: ROUTES.inventory.vendors, labelKey: 'navigation.vendors', feature: AppFeature.Vendor },
            { path: ROUTES.inventory.stores, labelKey: 'navigation.stores', feature: AppFeature.Store },
            { path: ROUTES.inventory.itemCategories, labelKey: 'navigation.itemCategories', feature: AppFeature.ItemCategory },
          ],
        },
        {
          labelKey: 'navigation.farm',
          children: [
            { path: ROUTES.estates.list, labelKey: 'navigation.estates', feature: AppFeature.Estate },
            { path: ROUTES.estates.blocks, labelKey: 'navigation.blocks', feature: AppFeature.Block },
            { path: ROUTES.estates.operationTypes, labelKey: 'navigation.operationTypes', feature: AppFeature.Operation },
            { path: ROUTES.estates.operationRates, labelKey: 'navigation.operationRates', feature: AppFeature.OperationRate },
            { path: ROUTES.estates.averageWeights, labelKey: 'navigation.averageWeights', feature: AppFeature.AverageWeight },
          ],
        },
        {
          labelKey: 'navigation.userManagement',
          children: [
            { path: ROUTES.administration.users, labelKey: 'navigation.users', feature: AppFeature.User },
            { path: ROUTES.administration.roles, labelKey: 'navigation.rolesPermissions', feature: AppFeature.Role },
          ],
        },
        {
          labelKey: 'navigation.general',
          children: [
            { path: ROUTES.administration.workingYears, labelKey: 'navigation.workingYears', feature: AppFeature.WorkingYear },
            { path: ROUTES.settings.momoOperators, labelKey: 'navigation.moMoOperators', feature: AppFeature.MoMoOperator },
            { path: ROUTES.settings.mobileProvision, labelKey: 'navigation.mobileProvision' },
            { path: ROUTES.settings.systemSettings, labelKey: 'navigation.systemSettings', feature: AppFeature.SystemSettings },
          ],
        },
        {
          labelKey: 'navigation.geography',
          children: [
            { path: ROUTES.geography.countries, labelKey: 'navigation.countries' },
            { path: ROUTES.geography.regions, labelKey: 'navigation.regions' },
            { path: ROUTES.geography.districts, labelKey: 'navigation.districts' },
          ],
        },
        {
          labelKey: 'navigation.sales',
          children: [
            { path: ROUTES.payroll.farmSaleConfig, labelKey: 'navigation.farmSaleConfig', feature: AppFeature.PayrollDeduction },
            { path: ROUTES.sales.saleTypes, labelKey: 'navigation.saleTypes', feature: AppFeature.SaleType },
            { path: ROUTES.sales.pricingTiers, labelKey: 'navigation.pricingTiers', feature: AppFeature.PricingTier },
            { path: ROUTES.sales.salePrices, labelKey: 'navigation.salePrices', feature: AppFeature.SalePrice },
            { path: ROUTES.sales.marketPrices, labelKey: 'navigation.marketPrices', feature: AppFeature.MarketPrice },
          ],
        },
        {
          labelKey: 'deductionTypes.deductions',
          children: [
            { path: ROUTES.setup.deductionTypes, labelKey: 'navigation.deductionTypes', feature: AppFeature.Deductions },
          ],
        },
        {
          labelKey: 'navigation.payrollSetup',
          children: [
            { path: ROUTES.setup.salaryGrid, labelKey: 'navigation.salaryGrid', feature: AppFeature.SalaryGrid },
            { path: ROUTES.setup.allowanceTypes, labelKey: 'navigation.allowanceTypes', feature: AppFeature.AllowanceType },
            { path: ROUTES.settings.statutoryRates, labelKey: 'navigation.statutoryRates', feature: AppFeature.StatutoryRates },
          ],
        },
      ],
    },
    {
      path: '/help',
      labelKey: 'common.help',
      icon: <HelpCircle className="h-4 w-4" aria-hidden="true" />,
    },
  ];

  return allItems.map((item) => filterItem(item, canRead)).filter(Boolean) as NavItem[];
}

// ---- Dropdown item — leaf link or cascading submenu trigger ----------------

function DropdownItem({
  item,
  pathname,
  closeAll,
}: {
  item: NavItem;
  pathname: string;
  closeAll: () => void;
}) {
  const { t } = useTranslation();
  const [subOpen, setSubOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openSub = () => {
    if (timer.current) clearTimeout(timer.current);
    setSubOpen(true);
  };
  const closeSub = () => {
    timer.current = setTimeout(() => setSubOpen(false), 150);
  };

  const itemLabel = t(item.labelKey);

  // Leaf node — plain link
  if (!item.children) {
    const active = item.path
      ? pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path))
      : false;
    return (
      <Link
        to={item.path!}
        role="menuitem"
        aria-current={pathname === item.path ? 'page' : undefined}
        onClick={closeAll}
        className={cn(
          'hover:bg-accent hover:text-accent-foreground flex w-full cursor-default items-center rounded-sm px-2 py-1.5 text-sm transition-colors outline-none select-none',
          active && 'text-primary font-medium'
        )}
      >
        {itemLabel}
      </Link>
    );
  }

  // Group node — submenu trigger
  const subActive = leafPaths(item).some(
    (p) => pathname === p || (p !== '/' && pathname.startsWith(p))
  );

  return (
    <div className="relative" onMouseEnter={openSub} onMouseLeave={closeSub}>
      <button
        aria-haspopup="true"
        aria-expanded={subOpen}
        className={cn(
          'hover:bg-accent hover:text-accent-foreground flex w-full cursor-default items-center justify-between rounded-sm px-2 py-1.5 text-sm transition-colors outline-none select-none',
          subActive && 'text-primary font-medium'
        )}
      >
        {itemLabel}
        <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </button>

      {/* Fly-out panel */}
      <div
        role="menu"
        aria-label={itemLabel}
        aria-hidden={!subOpen}
        inert={!subOpen || undefined}
        className={cn(
          'bg-popover text-popover-foreground absolute top-0 left-full z-50 ml-1 w-52 rounded-md border p-1 shadow-md',
          'transition-all duration-200 ease-out',
          subOpen
            ? 'pointer-events-auto translate-x-0 opacity-100'
            : 'pointer-events-none -translate-x-1 opacity-0'
        )}
      >
        {item.children.map((child) => (
          <DropdownItem
            key={child.path ?? child.labelKey}
            item={child}
            pathname={pathname}
            closeAll={closeAll}
          />
        ))}
      </div>
    </div>
  );
}

// ---- Top-nav item (with optional dropdown) ---------------------------------

function NavMenuEntry({ item, pathname }: { item: NavItem; pathname: string }) {
  const { t } = useTranslation();
  const label = t(item.labelKey);

  const ownActive = item.path
    ? pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path))
    : false;
  const deepActive = leafPaths(item).some(
    (p) => pathname === p || (p !== '/' && pathname.startsWith(p))
  );
  const isActive = ownActive || deepActive;

  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  if (!item.children) {
    return (
      <Link
        to={item.path!}
        aria-current={pathname === item.path ? 'page' : undefined}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-sidebar-primary/10 text-sidebar-primary'
            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
        )}
      >
        {item.icon}
        {label}
      </Link>
    );
  }

  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-sidebar-primary/10 text-sidebar-primary'
            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
        )}
      >
        {item.icon}
        {label}
        <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </button>

      <div
        role="menu"
        aria-label={label}
        aria-hidden={!open}
        inert={!open || undefined}
        className={cn(
          'bg-popover text-popover-foreground absolute top-full left-0 z-50 mt-1 w-52 rounded-md border p-1 shadow-md',
          'transition-all duration-200 ease-out',
          open
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-1 opacity-0'
        )}
      >
        {item.children.map((child) => (
          <DropdownItem
            key={child.path ?? child.labelKey}
            item={child}
            pathname={pathname}
            closeAll={() => setOpen(false)}
          />
        ))}
      </div>
    </div>
  );
}

// ---- Navbar ----------------------------------------------------------------

export function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const items = useNavItems();
  const pathname = location.pathname;
  const appName = t('common.appName');

  const userInitials = user?.fullName
    ? user.fullName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U';

  const handleLogout = async () => {
    try {
      await signOut();
    } catch {
      navigate('/login');
    }
  };

  return (
    <header className="bg-sidebar/95 supports-[backdrop-filter]:bg-sidebar/60 border-sidebar-border sticky top-0 z-40 w-full border-b backdrop-blur">
      <div className="text-sidebar-foreground mx-auto flex h-14 max-w-screen-2xl items-center gap-2 px-4">
        {/* Skip to main content */}
        <a
          href="#main-content"
          className="focus:bg-background focus:ring-primary sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-md focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-md focus:ring-2"
        >
          Skip to content
        </a>

        {/* Logo */}
        <Link
          to={ROUTES.root}
          className="flex shrink-0 items-center gap-2.5"
          aria-label={appName}
        >
          <div
            className="from-primary to-primary/70 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br"
            aria-hidden="true"
          >
            <span className="text-primary-foreground text-xs font-black">
              {appName
                .split(' ')
                .map((word) => word[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </span>
          </div>
          <span className="hidden font-semibold sm:inline-block">{appName}</span>
        </Link>

        <Separator
          orientation="vertical"
          className="bg-sidebar-foreground/20 mx-2 h-6"
          aria-hidden="true"
        />

        {/* Main nav */}
        <nav aria-label="Main navigation" className="flex flex-1 items-center gap-0.5">
          {items.map((item) => (
            <NavMenuEntry key={item.path ?? item.labelKey} item={item} pathname={pathname} />
          ))}
        </nav>

        {/* User menu */}
        <div className="flex shrink-0 items-center gap-1">
          <LanguageSwitcher />
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground relative h-8 w-8 rounded-full"
                aria-label={`User menu: ${user?.fullName ?? 'User'}`}
              >
                <div className="bg-sidebar-primary/20 text-sidebar-primary h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold">
                  {userInitials}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5">
                <p className="text-sm leading-none font-medium">{user?.fullName ?? 'User'}</p>
                {user?.email && (
                  <p className="text-muted-foreground mt-0.5 truncate text-xs">{user.email}</p>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link
                  to="/settings/preferences"
                  className="flex items-center cursor-pointer"
                >
                  <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
                  Preferences
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
